import React__default from "react";
import { FloatingPortal } from "@floating-ui/react";
import { CSSTransition } from "react-transition-group";
import { useAnalytics } from "../../helpers/analytics/analytics-provider/analytics-provider.mjs";
import { useTheme } from "../../helpers/theme-provider/use-theme.mjs";
import { Toast } from "../toast/toast.mjs";
import { Timer } from "./timer.mjs";
import { ToastContext } from "./toast-context.mjs";
const ToastProvider = ({ children }) => {
  const { toast, setToastData } = useCurrentToast();
  const { portalRoot } = useTheme();
  return /* @__PURE__ */ React__default.createElement(ToastContext.Provider, { value: React__default.useMemo(() => ({ setToastData }), [setToastData]) }, children, /* @__PURE__ */ React__default.createElement(FloatingPortal, { root: portalRoot }, /* @__PURE__ */ React__default.createElement(
    "div",
    {
      className: "toast-provider",
      role: "status",
      "aria-live": "polite"
    },
    toast
  )));
};
const TOAST_DURATION = 1e4;
const useCurrentToast = () => {
  const [toastData, setToastData] = React__default.useState(null);
  const [animationState, setAnimationState] = React__default.useState("paused");
  const toastRef = React__default.useRef(null);
  const [hasToastData, setHasToastData] = React__default.useState(false);
  const hasToastDataRef = React__default.useRef(false);
  const nextToastDataRef = React__default.useRef(null);
  const { push: pushAnalytics } = useAnalytics();
  const timer = React__default.useRef(new Timer(() => {
  }, TOAST_DURATION));
  const renderDelayTimeoutRef = React__default.useRef(0);
  const handleClose = React__default.useCallback(
    (event) => {
      event.preventDefault();
      if (toastData) {
        pushAnalytics("Toast - Close - ".concat(toastData.type), toastData.type, "close", {
          elementType: "toast",
          event: toastData.analyticsData.event,
          page: toastData.analyticsData.page,
          group: toastData.analyticsData.group
        });
      }
      timer.current.clear();
      setHasToastData(false);
      setAnimationState("paused");
      hasToastDataRef.current = false;
    },
    [toastData, pushAnalytics]
  );
  const handleMouseEnter = React__default.useCallback(() => {
    timer.current.pause();
    setAnimationState("paused");
  }, []);
  const handleMouseLeave = React__default.useCallback(() => {
    timer.current.resume();
    setAnimationState("running");
  }, []);
  React__default.useEffect(() => {
    const currentTimer = timer.current;
    const currentRenderDelayTimeout = renderDelayTimeoutRef.current;
    return () => {
      currentTimer.clear();
      window.clearTimeout(currentRenderDelayTimeout);
    };
  }, []);
  const showToast = React__default.useCallback(
    (data) => {
      window.clearTimeout(renderDelayTimeoutRef.current);
      renderDelayTimeoutRef.current = window.setTimeout(
        () => {
          pushAnalytics("Toast - Show - ".concat(data.type), data.type, "show", {
            elementType: "toast",
            event: data.analyticsData.event,
            page: data.analyticsData.page,
            group: data.analyticsData.group
          });
          setToastData(data);
          setHasToastData(true);
          hasToastDataRef.current = true;
        },
        // 150ms seems to be the minimum delay to ensure browsers and screenreaders (looking at you, macOS Safari + VoiceOver) properly announce sequential toasts with same content
        hasToastDataRef.current ? 150 : 1
      );
    },
    [pushAnalytics]
  );
  const handleEntered = React__default.useCallback(() => {
    timer.current.clear();
    setAnimationState("running");
    timer.current = new Timer(() => {
      setHasToastData(false);
      setAnimationState("paused");
    }, TOAST_DURATION);
  }, []);
  const handleExited = React__default.useCallback(() => {
    if (nextToastDataRef.current) {
      showToast(nextToastDataRef.current);
      nextToastDataRef.current = null;
    } else {
      setToastData(null);
      hasToastDataRef.current = false;
    }
  }, [showToast]);
  const toast = /* @__PURE__ */ React__default.createElement(
    CSSTransition,
    {
      nodeRef: toastRef,
      in: hasToastData,
      timeout: 300,
      classNames: "is",
      mountOnEnter: true,
      unmountOnExit: true,
      onEntered: handleEntered,
      onExited: handleExited
    },
    /* @__PURE__ */ React__default.createElement(
      Toast,
      {
        ref: toastRef,
        type: toastData == null ? void 0 : toastData.type,
        timeoutAnimationState: animationState,
        onClose: handleClose,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave
      },
      toastData ? toastData.content : ""
    )
  );
  const setToast = React__default.useCallback(
    (data) => {
      if (hasToastDataRef.current) {
        setHasToastData(false);
        setAnimationState("paused");
        nextToastDataRef.current = data;
      } else {
        showToast(data);
      }
    },
    [showToast]
  );
  return { toast, toastData, setToastData: setToast };
};
export {
  ToastProvider
};
